import { InputAdornment } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPriceTypeLabel, priceTypeEnum, stepModeEnum, systemConfigKeyEnum } from '../../../../constants';
import { useCreateDailyTempWorkerMutation, useDailyTempWorkerMutation } from '../../../../hooks/mutations/useDailyTempWorkerMutation';
import { useTempWorkersQuery } from '../../../../hooks/queries/useTempWorkersQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DataItem } from '../../../../services/apiSLTP/dailyReports/activities';
import { DailyTempWorker } from '../../../../services/apiSLTP/dailyReports/dailyTempWorkers';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import IconButton from '../../../ui/IconButton';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageDailyTempWorkerModalProps extends ModalProps {
    activity: DataItem;
    refetch: () => void;
    dailyTempWorker: DailyTempWorker | null;
}

const ManageDailyTempWorkerModal: React.FC<ManageDailyTempWorkerModalProps> = ({ open, handleClose, activity, refetch, dailyTempWorker }) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [selectedTempWorkerType, setSelectedTempWorkerType] = useState<OptionType | null>(null);
    const [currentTempWorkerId, setCurrentTempWorkerId] = useState(dailyTempWorker?._id);
    const [tempWorkerName, setTempWorkerName] = useState('');
    const [durationHours, setDurationHours] = useState('7');
    const [price, setPrice] = useState(typeof dailyTempWorker?.price === 'number' ? dailyTempWorker?.price.toString() : '');
    const [priceType, setPriceType] = useState<OptionType | null>(
        dailyTempWorker?.priceType
            ? {
                  label: capitalizeFirstLetter(t(getPriceTypeLabel(dailyTempWorker?.priceType))),
                  value: (dailyTempWorker?.priceType).toString(),
              }
            : null
    );

    const { data: dataTempWorkerTypes } = useTempWorkersQuery({ queries: { active: true } });
    const { mutate: updateDailyTempWorker } = useDailyTempWorkerMutation({ onSuccess: refetch });
    const { mutate: createDailyTempWorker } = useCreateDailyTempWorkerMutation({ onSuccess: refetch });

    const postDailyTempWorker = () => {
        if (!selectedTempWorkerType) return;
        createDailyTempWorker({
            body: {
                activityId: activity._id,
                tempWorkerTypeId: selectedTempWorkerType.value,
                durationHours: parseFloat(durationHours),
                tempWorkerName: tempWorkerName,
                price: price ? parseFloat(price) : undefined,
                priceType: priceType?.value ? parseInt(priceType.value) : undefined,
            },
        });
    };

    const patchDailyTempWorker = () => {
        if (currentTempWorkerId) {
            updateDailyTempWorker({
                id: currentTempWorkerId,
                body: {
                    tempWorkerTypeId: selectedTempWorkerType?.value,
                    durationHours: parseFloat(durationHours),
                    tempWorkerName: tempWorkerName,
                    price: price ? parseFloat(price) : null,
                    priceType: priceType?.value ? parseInt(priceType.value) : null,
                },
            });
        }
    };

    useEffect(() => {
        if (dailyTempWorker) {
            setSelectedTempWorkerType({
                label: dailyTempWorker.tempWorkerTypeId?.designation,
                value: dailyTempWorker.tempWorkerTypeId?._id,
            });
            setTempWorkerName(dailyTempWorker.tempWorkerName || '');
            setDurationHours((dailyTempWorker.durationHours ?? 8).toString());
        }
    }, []);

    return (
        <ModalBody
            title={t('tempWorker')}
            subtitle={!!currentTempWorkerId?.length ? t('update') : t('add')}
            description={activity.siteId.name}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                !currentTempWorkerId?.length ? postDailyTempWorker() : patchDailyTempWorker();
            }}
            disableSubmit={!(selectedTempWorkerType?.value.length && tempWorkerName.length && durationHours.length)}
        >
            <DropDown
                selectedItem={selectedTempWorkerType}
                items={dataTempWorkerTypes?.map((tempWorker) => ({ label: tempWorker.designation, value: tempWorker._id })) || []}
                handleChange={(event, value) => setSelectedTempWorkerType(value)}
                inputLabel={t('company')}
                required
            />
            <CustomTextInput
                value={tempWorkerName}
                onChange={(text) => setTempWorkerName(text)}
                label={t('tempWorker')}
                required
                placeholder={`${formatName('bertrand', 'dupont')}`}
            />

            <CustomTextInput
                label={capitalizeFirstLetter(t('durationHours'))}
                type="number"
                value={durationHours}
                onChange={(text) => setDurationHours(text)}
                required
                startAdornment={
                    <InputAdornment position="start">
                        <IconButton
                            iconName="remove"
                            disabled={parseFloat(durationHours) <= 0}
                            onClick={() => setDurationHours(Math.max(0, (parseFloat(durationHours) || 0.5) - 0.5).toString())}
                        />
                    </InputAdornment>
                }
                endAdornment={
                    <InputAdornment position="end">
                        <IconButton
                            iconName="add"
                            disabled={parseFloat(durationHours) >= 24}
                            onClick={() => setDurationHours(Math.min(24, (parseFloat(durationHours) || 0) + 0.5).toString())}
                        />
                    </InputAdornment>
                }
            />
            {activity.stepMode === stepModeEnum.VALIDATE && (
                <>
                    <CustomTextInput
                        value={price}
                        onChange={setPrice}
                        label={t('price')}
                        errorText={t('error')}
                        startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                        type="number"
                    />
                    <DropDown
                        items={Object.values(priceTypeEnum).map((type) => ({
                            label: capitalizeFirstLetter(t(getPriceTypeLabel(type))),
                            value: type.toString(),
                        }))}
                        selectedItem={priceType}
                        handleChange={(e, value) => {
                            setPriceType(value);
                        }}
                        inputLabel={t('pricing')}
                        disableClearable={false}
                    />
                </>
            )}
        </ModalBody>
    );
};

export default ManageDailyTempWorkerModal;
